import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { Lead } from '@/types/lead';
import { toggleFavorite, deleteLead } from '@/services/leads';
import { addNote } from '@/services/leads/noteService';
import { addDocument } from '@/services/leads/documentService';
import { useLoading } from '@/context/LoadingContext';

export const useLeadActions = (setLeads: React.Dispatch<React.SetStateAction<Lead[]>>) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { startLoading, stopLoading } = useLoading();
  const [operationInProgress, setOperationInProgress] = useState<Set<string>>(new Set());

  const handleToggleFavorite = async (id: string) => {
    if (operationInProgress.has(id)) return;
    
    const operationId = `toggle-favorite-${id}`;
    setOperationInProgress(prev => new Set(prev).add(id));
    startLoading(operationId, 'Updating favorite...');
    
    try {
      const updatedLead = await toggleFavorite(id);
      setLeads(prev => prev.map(lead => lead.id === id ? { ...lead, isFavorite: updatedLead.isFavorite } : lead));
      
      toast({
        title: updatedLead.isFavorite ? "Added to Favorites" : "Removed from Favorites",
        description: `${updatedLead.name} has been ${updatedLead.isFavorite ? 'added to' : 'removed from'} your favorites.`,
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update favorite status.",
      });
    } finally {
      stopLoading(operationId);
      setOperationInProgress(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (operationInProgress.has(id)) return;
    
    const operationId = `delete-lead-${id}`;
    setOperationInProgress(prev => new Set(prev).add(id));
    startLoading(operationId, 'Deleting lead...');
    
    try {
      let leadName = "Lead";
      setLeads(prev => {
        const leadToDelete = prev.find(lead => lead.id === id);
        if (leadToDelete) {
          leadName = leadToDelete.name;
        }
        return prev;
      });
      
      const result = await deleteLead(id);
      
      if (result.success) {
        setLeads(prev => prev.filter(lead => lead.id !== id));
        
        // Navigate away if currently viewing the deleted lead
        if (location.pathname.includes(`/business/${id}`)) {
          navigate('/app');
        }
        
        toast({
          title: "Lead Deleted",
          description: `${leadName} has been deleted.`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Permission Denied",
          description: result.error || "You can only delete leads that you have created.",
        });
      }
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete lead.",
      });
    } finally {
      stopLoading(operationId);
      setOperationInProgress(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleAddNote = async (leadId: string, content: string) => {
    if (operationInProgress.has(`note-${leadId}`)) return;
    
    const operationId = `add-note-${leadId}`;
    setOperationInProgress(prev => new Set(prev).add(`note-${leadId}`));
    startLoading(operationId, 'Adding note...');
    
    try {
      const newNote = await addNote(leadId, content);
      setLeads(prev => prev.map(lead => 
        lead.id === leadId 
          ? { ...lead, notes: [...lead.notes, newNote] } 
          : lead
      ));
      
      toast({
        title: "Note Added",
        description: "Your note has been added successfully.",
      });
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add note.",
      });
    } finally {
      stopLoading(operationId);
      setOperationInProgress(prev => {
        const next = new Set(prev);
        next.delete(`note-${leadId}`);
        return next;
      });
    }
  };

  const handleAddDocument = async (leadId: string, name: string, url: string, type: string) => {
    if (operationInProgress.has(`doc-${leadId}`)) return;
    
    const operationId = `add-document-${leadId}`;
    setOperationInProgress(prev => new Set(prev).add(`doc-${leadId}`));
    startLoading(operationId, 'Adding document...');
    
    try {
      const newDocument = await addDocument(leadId, name, url, type);
      setLeads(prev => prev.map(lead => 
        lead.id === leadId 
          ? { ...lead, documents: [...lead.documents, newDocument] } 
          : lead
      ));
      
      toast({
        title: "Document Added",
        description: "Your document has been added successfully.",
      });
    } catch (error) {
      console.error('Error adding document:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add document.",
      });
    } finally {
      stopLoading(operationId);
      setOperationInProgress(prev => {
        const next = new Set(prev);
        next.delete(`doc-${leadId}`);
        return next;
      });
    }
  };

  return {
    handleToggleFavorite,
    handleDelete,
    handleAddNote,
    handleAddDocument
  };
};
